import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { identifyUser, resetUser, trackEvent } from "./analytics";
import { trackPulseEvent, consumePulseSource } from "./qelliPulseFunnel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserAuthCtx = createContext({ account: null, loading: true, signup: async () => {}, login: async () => {}, logout: async () => {}, refresh: async () => {} });

/**
 * Build the property bag we send to PostHog `identify`. Keep it small and
 * non-PII-heavy: email is fine for B2B SaaS, but skip anything we wouldn't
 * want appearing in an analytics dashboard column.
 */
function identifyProps(account) {
  if (!account) return {};
  return {
    email:        account.email,
    plan:         account.plan,
    org_id:       account.org_id,
    marketing_opt_in: account.marketing_opt_in,
    created_at:   account.created_at,
    // Phase 1 — persona props on every identify call so PostHog cohorts
    // attribute analytics events correctly. Silent when fields are unset.
    persona_type:     account.persona_type,
    role:             account.role,
    company_size:     account.company_size,
    enterprise_track: account.enterprise_track,
  };
}

export function UserAuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  const client = useMemo(() => axios.create({
    baseURL: API,
    withCredentials: true,
  }), []);

  const refresh = async () => {
    // Iter55 — /account/me is for public consumer auth (qt_user_token cookie).
    // Suppress on admin routes so admin sessions don't surface a spurious
    // 401 → "Something went wrong" toast on every /admin/* page paint.
    if (typeof window !== "undefined"
        && window.location.pathname.startsWith("/admin")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await client.get("/account/me");
      const a = data.account || null;
      setAccount(a);
      // Re-identify on every cold-start refresh so analytics events
      // captured before /account/me resolves are still attributable.
      if (a) identifyUser(a.id, identifyProps(a));
    } catch (_) {
      setAccount(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const signup = async (payload) => {
    const { data } = await client.post("/account/signup", payload);
    setAccount(data.account);
    if (data.account) {
      identifyUser(data.account.id, identifyProps(data.account));
      trackEvent("account_signup", { plan: data.account.plan });
      // Iter199 — close the Qelli pulse-funnel loop. If a pulse was
      // shown to this visitor in the current session, attribute the
      // signup back to that source. Single-use: consumed on read.
      const pulseSource = consumePulseSource();
      if (pulseSource) {
        trackPulseEvent(pulseSource, "signup");
      }
    }
    return data.account;
  };

  const login = async (payload) => {
    const { data } = await client.post("/account/login", payload);
    setAccount(data.account);
    if (data.account) {
      identifyUser(data.account.id, identifyProps(data.account));
      trackEvent("account_login", { plan: data.account.plan });
    }
    return data.account;
  };

  const logout = async () => {
    try { await client.post("/account/logout"); } catch (_) {}
    setAccount(null);
    // Clear PostHog identity so subsequent anonymous events on the
    // shared device aren't stitched to the previous user's profile.
    resetUser();
  };

  const setMarketing = async (opt_in) => {
    const { data } = await client.post("/account/marketing", { opt_in });
    setAccount((a) => ({
      ...(a || {}),
      marketing_opt_in: data.marketing_opt_in,
      marketing_prefs:  data.marketing_prefs ?? (a || {}).marketing_prefs,
    }));
    return data.marketing_opt_in;
  };

  // Iter77 — flip a single per-cadence pref (verify_recap, annual_upsell,
  // feature_announcements). Master `marketing_opt_in` stays unchanged.
  const setMarketingPref = async (cadence, value) => {
    const { data } = await client.post("/account/marketing", { cadence, value });
    setAccount((a) => ({
      ...(a || {}),
      marketing_opt_in: data.marketing_opt_in,
      marketing_prefs:  data.marketing_prefs ?? (a || {}).marketing_prefs,
    }));
    return data.marketing_prefs;
  };

  return (
    <UserAuthCtx.Provider value={{ account, loading, client, signup, login, logout, refresh, setMarketing, setMarketingPref }}>
      {children}
    </UserAuthCtx.Provider>
  );
}

export function useUserAuth() { return useContext(UserAuthCtx); }
